import dayjs from "dayjs";

import { formatReceivedDate, createRequest, prune } from "../../utils/utility";
import { ID } from "../types";
import { Investment } from "../investment/types";
import { Payment } from "../payment/types";
import { Transaction } from "../transaction/types";
import { ValidationMessages, ExperienceType, ExperienceLevel, ForEach } from "common/utils/types";
import { validateRequiredNotDefault, validateCellphones, validateBooleansTrue, CustomValidationArguments, validateDates } from "common/utils/validation";
import { randomDate, randomBoolean, randomSelectOption, randomNumber, randomAutocompleteOption, randomString, randomNoun, randomAdjective } from "common/utils/random";
import { SelectOptions } from "common/assets/data/options";

export class User {
  id: ID = User.default().id;
  account: string = User.default().account;
  email: string = User.default().email;
  profile: boolean = User.default().profile;
  whitelisted: boolean = User.default().whitelisted;
  investments: Investment[] = User.default().investments;
  firstName: string = User.default().firstName;
  lastName: string = User.default().lastName;
  cellphone: string = User.default().cellphone;
  birthDate: string = User.default().birthDate;
  birthCity: string = User.default().birthCity;
  gender: string = User.default().gender;
  citizenship: any = User.default().citizenship;
  taxNumber: string = User.default().taxNumber;
  notUSTax: boolean = User.default().notUSTax;
  politicallyExposed: string = User.default().politicallyExposed;
  idType: string = User.default().idType;
  idDocumentNumber: string = User.default().idDocumentNumber;
  idDocumentCountry: string = User.default().idDocumentCountry;
  passportIssuingAgency: string = User.default().passportIssuingAgency;
  address: string = User.default().address;
  addressCity: string = User.default().addressCity;
  addressZipcode: string = User.default().addressZipcode;
  addressCountry: string = User.default().addressCountry;
  employment: string = User.default().employment;
  prevEmployment: string = User.default().prevEmployment;
  highestEducation: string = User.default().highestEducation;
  occupationGroup: string = User.default().occupationGroup;
  experience: Array<ExperienceLevel> = User.default().experience;
  payment_accounts: Payment[] = User.default().payment_accounts;
  default_payment: ID = User.default().default_payment;
  idNowTransactionId: string = User.default().idNowTransactionId;
  idNowId: string = User.default().idNowId;
  idNowStatus: string = User.default().idNowStatus;
  transactions: Transaction[] = User.default().transactions;
  notProvidingExperience: boolean = User.default().notProvidingExperience;
  transactionsPerYear: Array<number> = User.default().transactionsPerYear;
  volumePerYear: Array<number> = User.default().volumePerYear;
  availableCash: number = User.default().availableCash;
  monthlySalary: number = User.default().monthlySalary;

  static readonly addFields : Array<string> = [
    "firstName",
    "lastName",
    "gender",
    "cellphone",
    "birthDate",
    "birthCity",
    "citizenship",
    "politicallyExposed",
    "taxNumber",
    "notUSTax",
    "address",
    "addressZipcode",
    "addressCity",
    "addressCountry",
    "idType",
    "idDocumentNumber",
    "idDocumentCountry",
    "passportIssuingAgency",
    "experience",
    "employment",
    "prevEmployment",
    "highestEducation",
    "occupationGroup",
    "transactionsPerYear",
    "volumePerYear",
    "availableCash",
    "monthlySalary",
  ];

  static readonly updateFields : Array<string> = [
    "cellphone",
    "address",
    "addressZipcode",
    "addressCity",
    "addressCountry",
    "experience",
    "employment",
    "prevEmployment",
    "highestEducation",
    "occupationGroup",
    "transactionsPerYear",
    "volumePerYear",
    "availableCash",
    "monthlySalary",
  ];

  static readonly adminUpdateFields : Array<string> = [
    "address",
    "addressCity",
    "addressCountry",
    "addressZipcode",
    "birthCity",
    "birthDate",
    "citizenship",
    "firstName",
    "gender",
    "id",
    "lastName",
  ];

  constructor(obj: any) {
    obj && Object.assign(this, prune(obj));

    this.birthDate = formatReceivedDate(
      this.birthDate
    );

    const accounts = [];
    for (let i = 0; i < this.payment_accounts.length; i++) {
      accounts.push(new Payment(this.payment_accounts[i]));
    }
    this.payment_accounts = accounts;

    const investments = [];
    for (let i = 0; i < this.investments.length; i++) {
      investments.push(new Investment(this.investments[i]));
    }
    this.investments = investments;
  }

  hasExperience(type?: ExperienceType) {
    const noExperience = SelectOptions().experience[0].value;
    const defaultExperience = "";
    const hasXp = (xp: any) => xp !== noExperience && xp !== defaultExperience;

    return !this.notProvidingExperience && (
      type !== undefined ?
      hasXp(this.experience[type]) :
      new Array(Object.keys(ExperienceType).length / 2).reduce((result, _, index) => result || hasXp(this.experience[index]), false)
    );
  }

  required(): any {
    return {
      id: false,
      account: false,
      email: false,
      profile: false,
      whitelisted: false,
      investments: false,
      firstName: true,
      lastName: true,
      cellphone: true,
      birthDate: true,
      birthCity: true,
      gender: true,
      citizenship: true,
      taxNumber: true,
      notUSTax: true,
      politicallyExposed: true,
      idType: true,
      idDocumentNumber: true,
      idDocumentCountry: true,
      passportIssuingAgency: true,
      address: true,
      addressCity: true,
      addressZipcode: true,
      addressCountry: true,
      employment: true,
      prevEmployment: true,
      highestEducation: true,
      occupationGroup: true,
      experience: ForEach("ExperienceType", () => !this.notProvidingExperience),
      payment_accounts: false,
      default_payment: false,
      idNowTransactionId: false,
      idNowId: false,
      idNowStatus: false,
      transactions: false,
      notProvidingExperience: false,
      transactionsPerYear: ForEach("ExperienceType", (x) => this.hasExperience(x)),
      volumePerYear: ForEach("ExperienceType", (x) => this.hasExperience(x)),
      availableCash: false,
      monthlySalary: false,
    };
  }

  static default(): any {
    return {
      id: "",
      account: "",
      email: "",
      profile: false,
      whitelisted: false,
      investments: [],
      firstName: "",
      lastName: "",
      cellphone: "",
      birthDate: "",
      birthCity: "",
      gender: "",
      citizenship: "",
      taxNumber: "",
      notUSTax: false,
      politicallyExposed: "",
      idType: "",
      idDocumentNumber: "",
      idDocumentCountry: "",
      passportIssuingAgency: "",
      address: "",
      addressCity: "",
      addressZipcode: "",
      addressCountry: "",
      employment: "",
      prevEmployment: "",
      highestEducation: null,
      occupationGroup: null,
      experience: ForEach("ExperienceType", () => ""),
      payment_accounts: [],
      default_payment: "",
      idNowTransactionId: null,
      idNowId: null,
      idNowStatus: null,
      transactions: [],
      notProvidingExperience: false,
      transactionsPerYear: ForEach("ExperienceType", () => 0),
      volumePerYear: ForEach("ExperienceType", () => 0),
      availableCash: 0,
      monthlySalary: 0,
    };
  }

  static automatic(): any {
    const notProvidingExperience = randomBoolean();
    const noExperience = SelectOptions().experience[0].value;

    return {
      ...User.default(),
      firstName: randomAdjective(),
      lastName: randomNoun(),
      cellphone: "+49232323543",
      birthDate: randomDate(),
      birthCity: "X",
      gender: randomSelectOption('gender'),
      citizenship: randomAutocompleteOption('countries'),
      taxNumber: "X",
      notUSTax: true,
      politicallyExposed: randomSelectOption('yesNo'),
      idType: randomSelectOption('idType'),
      idDocumentNumber: randomString(6),
      idDocumentCountry: randomAutocompleteOption('countries'),
      passportIssuingAgency: "X",
      address: "X",
      addressCity: "X",
      addressZipcode: "X",
      addressCountry: randomAutocompleteOption('countries'),
      employment: "X",
      prevEmployment: "X",
      highestEducation: randomAutocompleteOption('educationLevel'),
      occupationGroup: randomAutocompleteOption('occupationGroup'),
      experience: ForEach("ExperienceType", () => notProvidingExperience ? noExperience : randomSelectOption('experience')),
      notProvidingExperience: notProvidingExperience,
      transactionsPerYear: ForEach("ExperienceType", () => notProvidingExperience ? 0 : randomNumber(0, 1000)),
      volumePerYear: ForEach("ExperienceType", () => notProvidingExperience ? 0 : randomNumber(0, 1000)),
    };
  }

  validate(keys: Array<string>, messages: any): any {
    const shouldValidate = (key: string): boolean => {
      return keys.includes(key);
    };

    let errors: any = {};

    validateCellphones(new CustomValidationArguments({
      keys: ['cellphone'],
      errors,
      values: this,
      allowedKeys: keys,
      errorMessage: messages[ValidationMessages.Invalid]
    }));

    validateBooleansTrue(new CustomValidationArguments({
      keys: ['notUSTax'],
      errors,
      values: this,
      allowedKeys: keys,
      errorMessage: messages[ValidationMessages.Required]
    }));

    validateDates(new CustomValidationArguments({
      keys: ['birthDate'],
      errors,
      values: this,
      allowedKeys: keys,
      errorMessage: messages[ValidationMessages.Required]
    }));

    validateRequiredNotDefault(
      errors,
      this.required(),
      this,
      User.default(),
      keys,
      messages[ValidationMessages.Required]
    );

    if (shouldValidate("birthDate")) {
      let maxBirthDate = new Date();
      maxBirthDate.setFullYear(maxBirthDate.getFullYear() - 18);
      if (!errors.birthDate && dayjs(this.birthDate).isAfter(maxBirthDate)) {
        errors.birthDate = messages[ValidationMessages.Invalid];
      }
    }

    if (shouldValidate("default_payment") &&
        !this.payment_accounts.find((account: Payment) => account.id === this.default_payment)) {
      errors.default_payment = messages[ValidationMessages.Invalid];
    }

    return errors;
  }

  private createRequest(fields: Array<string>): any {
    return createRequest(this, fields);
  }

  addRequest(skipIdNow: boolean): any {
    let data: any =  this.createRequest(User.addFields);

    if (dayjs(data.birthDate).isValid()) {
      data.birthDate = dayjs(data.birthDate).toISOString();
    }

    data.skipIdNow = skipIdNow;

    if (this.notProvidingExperience) {
      const noExperience = SelectOptions().experience[0].value;
      data.experience = ForEach("ExperienceType", () => noExperience);
    }

    return data;
  }

  updateRequest(): any {
    let data: any = this.createRequest(User.updateFields);

    return data;
  }

  adminUpdateRequest(idNowApproval: boolean): any {
    let data: any = this.createRequest(User.adminUpdateFields);

    if (dayjs(data.birthDate).isValid()) {
      data.birthDate = dayjs(data.birthDate).toISOString();
    }

    data.idNowApproval = idNowApproval;

    return data;
  }

  userKnown(): boolean {
    return this.idNowStatus !== User.default().idNowStatus;
  }

  userVerified(): boolean {
    return this.idNowStatus === "SUCCESS";
  }
}